import { useMemo, useState } from "react";
import { App } from "antd";
import type { TableProps } from "antd";
import {
  CheckCircleOutlined,
  ClusterOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  OrderedListOutlined,
  PaperClipOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
  UserOutlined,
} from "@ant-design/icons";

import {
  DataTable,
  MenuThaoTac,
  PageHeader,
  StatTile,
} from "../../../components/shared";
import { Button, Empty, Input, Select } from "../../../components/ui";
import { useDieuHuong } from "../../../hooks/useDuongDan";
import { useLoi } from "../../../hooks/useLoi";
import { useT } from "../../../i18n";
import { KHONG_CO, ngayVN, soVN } from "../../../lib/dinh-dang";
import { NhiemVuTayDialog } from "../components/phan-cong/NhiemVuTayDialog";
import { IconLoaiDonVi } from "../components/phan-cong/IconLoaiDonVi";
import { PhanCongTepDialog } from "../components/phan-cong/PhanCongTepDialog";
import {
  LOAI_DON_VI,
  NHAN_LOAI_DON_VI,
  NHAN_TRANG_THAI_NHIEM_VU,
  TRANG_THAI_NHIEM_VU,
} from "../constants";
import {
  useDocNhiemVu,
  useLuuNhiemVuTay,
  useMoTepNhiemVu,
  useNhiemVuPhanCong,
  useXoaNhiemVuPhanCong,
} from "../hooks/usePhanCong";
import { useQuyenVanBan } from "../hooks/useQuyenVanBan";
import { useDonVi } from "../hooks/useVanBan";
import type { LoaiDonVi } from "../types";
import type { NhiemVuPhanCong, TrangThaiNhiemVu } from "../types-phan-cong";

const TRICH_TOI_DA = 120;

/**
 * Phân công nhiệm vụ (`/xu-ly-van-ban/phan-cong`), chuyển từ foxainative: thống kê, bảng nhiệm vụ đã
 * giao (lọc theo từ khoá, loại đối tượng, trạng thái), thêm tay / sửa kèm tệp, AI phân công từ tệp tải
 * lên. Phân công từ văn bản đã tóm tắt mở ở màn Tóm tắt văn bản.
 */
export function PhanCongView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const { modal } = App.useApp();
  const dieuHuong = useDieuHuong();
  const { data, isLoading, error, refetch } = useNhiemVuPhanCong();
  const donVi = useDonVi();
  const doc = useDocNhiemVu();
  const luu = useLuuNhiemVuTay();
  const xoa = useXoaNhiemVuPhanCong();
  const moTep = useMoTepNhiemVu();
  const { canCreate, canUpdate, canDelete } = useQuyenVanBan("task-assignment");

  const [tuKhoa, setTuKhoa] = useState("");
  const [loai, setLoai] = useState<LoaiDonVi | undefined>();
  const [trangThai, setTrangThai] = useState<TrangThaiNhiemVu | undefined>();
  const [moForm, setMoForm] = useState(false);
  const [dangSua, setDangSua] = useState<NhiemVuPhanCong | null>(null);
  const [dangDocId, setDangDocId] = useState<string | null>(null);
  const [moTai, setMoTai] = useState(0);

  const rows = useMemo(() => {
    const q = tuKhoa.trim().toLowerCase();
    return [...(data?.data ?? [])]
      .filter(
        (a) =>
          (!q ||
            a.task_content.toLowerCase().includes(q) ||
            (a.summarization_name ?? a.related_document ?? "")
              .toLowerCase()
              .includes(q) ||
            (a.unit_name ?? "").toLowerCase().includes(q)) &&
          (!loai || a.unit_type === loai) &&
          (!trangThai || a.status === trangThai),
      )
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
  }, [data, tuKhoa, loai, trangThai]);
  const coLoc = Boolean(tuKhoa.trim() || loai || trangThai);
  const thongKe = data?.stats;
  const so = (n: number | undefined) => soVN(n ?? 0, locale);

  const moThem = () => {
    luu.reset();
    setDangSua(null);
    setMoForm(true);
  };
  // Sửa = đọc bản mới nhất qua get-by-id rồi đổ vào chung dialog thêm/sửa, như bản cũ
  const moSua = (id: string) => {
    setDangDocId(id);
    doc.mutate(id, {
      onSuccess: (nv) => {
        luu.reset();
        setDangSua(nv);
        setMoForm(true);
      },
      onSettled: () => setDangDocId(null),
    });
  };
  const hoiXoa = (a: NhiemVuPhanCong) => {
    const trich =
      a.task_content.length > TRICH_TOI_DA
        ? `${a.task_content.slice(0, TRICH_TOI_DA)}…`
        : a.task_content;
    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: t("vanBanPhanCong.xacNhanXoa", { ten: a.unit_name ?? KHONG_CO }),
      content: (
        <div className="space-y-2">
          <p className="text-content-secondary">{trich}</p>
          <p>{t("vanBanPhanCong.xacNhanXoaMo")}</p>
        </div>
      ),
      okText: t("vanBanPhanCong.nutXoa"),
      okButtonProps: { danger: true },
      cancelText: t("chung.huy"),
      onOk: () => xoa.mutateAsync(a.id),
    });
  };

  const columns: TableProps<NhiemVuPhanCong>["columns"] = [
    {
      title: t("chung.stt"),
      width: 60,
      render: (_v, _r, i) => (
        <span className="text-xs tabular-nums text-content-muted">{i + 1}</span>
      ),
    },
    {
      title: t("vanBanPhanCong.cot.nhiemVu"),
      dataIndex: "task_content",
      render: (task: string) => (
        <span className="block min-w-48 max-w-md whitespace-pre-line leading-snug text-content-primary">
          {task}
        </span>
      ),
    },
    {
      title: t("vanBanPhanCong.cot.doiTuong"),
      width: 210,
      render: (_v, a) => (
        <span className="flex items-center gap-1.5 font-medium text-content-primary">
          {a.unit_type && <IconLoaiDonVi loai={a.unit_type} mau />}
          <span className="min-w-0">{a.unit_name ?? KHONG_CO}</span>
        </span>
      ),
    },
    {
      title: t("vanBanPhanCong.cot.vanBanNguon"),
      width: 210,
      render: (_v, a) => (
        <span className="block max-w-60 text-xs leading-snug text-content-secondary">
          <FileTextOutlined className="mr-1 text-content-muted" aria-hidden />
          {a.summarization_name ?? a.related_document ?? KHONG_CO}
        </span>
      ),
    },
    {
      title: t("vanBanPhanCong.cot.dinhKem"),
      width: 200,
      render: (_v, a) =>
        a.attachment_filename ? (
          <div
            className="flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() =>
                void moTep(a.id, a.attachment_filename as string, "xem")
              }
              title={t("vanBanPhanCong.xemTep", { ten: a.attachment_filename })}
              className="inline-flex min-w-0 max-w-40 items-center gap-1 rounded border border-primary-100 bg-primary-50 px-2 py-0.5 text-xs text-primary-700 hover:bg-primary-100"
            >
              <PaperClipOutlined className="shrink-0" aria-hidden />
              <span className="truncate">{a.attachment_filename}</span>
            </button>
            <Button
              variant="ghost"
              size="sm"
              icon={<DownloadOutlined />}
              title={t("vanBanPhanCong.taiTep")}
              aria-label={t("vanBanPhanCong.taiTepTen", {
                ten: a.attachment_filename,
              })}
              onClick={() =>
                void moTep(a.id, a.attachment_filename as string, "tai")
              }
            />
          </div>
        ) : (
          <span className="text-content-muted">{KHONG_CO}</span>
        ),
    },
    {
      title: t("vanBanPhanCong.cot.han"),
      dataIndex: "deadline",
      width: 120,
      render: (d: string | null) => (
        <span className="whitespace-nowrap text-xs text-content-secondary">
          {ngayVN(d)}
        </span>
      ),
    },
    {
      title: t("chung.thaoTac"),
      width: 72,
      align: "center",
      render: (_v, a) => (
        <div onClick={(e) => e.stopPropagation()}>
          <MenuThaoTac
            tenBanGhi={a.task_content.slice(0, 60)}
            muc={[
              {
                khoa: "sua",
                nhan: t("vanBanPhanCong.sua"),
                icon: <EditOutlined className="text-primary-600" />,
                hien: canUpdate,
                nhom: "sua",
                onChon: () => moSua(a.id),
              },
              {
                khoa: "xoa",
                nhan: t("vanBanPhanCong.xoa"),
                icon: <DeleteOutlined />,
                hien: canDelete,
                nhom: "pha-huy",
                onChon: () => hoiXoa(a),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[
            { label: t("nav.nhom.xuLyVanBan") },
            { label: t("vanBanPhanCong.tieuDe") },
          ]}
          titleIcon={<OrderedListOutlined className="text-primary-600" />}
          title={t("vanBanPhanCong.tieuDe")}
          subtitle={t("vanBanPhanCong.moTa")}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              {canCreate && (
                <Button
                  variant="outline"
                  icon={<PlusOutlined />}
                  onClick={moThem}
                >
                  {t("vanBanPhanCong.nutThemTay")}
                </Button>
              )}
              {canCreate && (
                <Button
                  variant="primary"
                  icon={<UploadOutlined />}
                  onClick={() => setMoTai((k) => k + 1)}
                >
                  {t("vanBanPhanCong.nutTaiTep")}
                </Button>
              )}
              <Button
                variant="ghost"
                icon={<FileSearchOutlined />}
                onClick={() => dieuHuong("/xu-ly-van-ban/tom-tat")}
              >
                {t("vanBanPhanCong.nutTuTomTat")}
              </Button>
            </div>
          }
        />

        <div className="grid grid-cols-12 gap-4">
          <StatTile
            span="col-span-12 sm:col-span-6 lg:col-span-3"
            nhan={t("vanBanPhanCong.thongKe.tong")}
            so={so(thongKe?.total)}
            icon={<OrderedListOutlined />}
            tone="primary"
          />
          <StatTile
            span="col-span-12 sm:col-span-6 lg:col-span-3"
            nhan={t("vanBanPhanCong.thongKe.phongBan")}
            so={so(thongKe?.phong_ban)}
            icon={<ClusterOutlined />}
            tone="teal"
          />
          <StatTile
            span="col-span-12 sm:col-span-6 lg:col-span-3"
            nhan={t("vanBanPhanCong.thongKe.caNhan")}
            so={so(thongKe?.ca_nhan)}
            icon={<UserOutlined />}
            tone="warning"
          />
          <StatTile
            span="col-span-12 sm:col-span-6 lg:col-span-3"
            nhan={t("vanBanPhanCong.thongKe.hoanThanh")}
            so={so(thongKe?.completed)}
            icon={<CheckCircleOutlined />}
            tone="success"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
          <Input
            className="min-w-[200px] flex-1"
            allowClear
            prefix={<SearchOutlined className="text-primary-500" />}
            placeholder={t("vanBanPhanCong.timTheo")}
            aria-label={t("vanBanPhanCong.timTheo")}
            value={tuKhoa}
            onChange={(e) => setTuKhoa(e.target.value)}
          />
          <Select
            allowClear
            className="min-w-[10rem]"
            placeholder={t("vanBanPhanCong.tatCaDoiTuong")}
            aria-label={t("vanBanPhanCong.locDoiTuong")}
            options={LOAI_DON_VI.map((value) => ({
              value,
              label: t(NHAN_LOAI_DON_VI[value]),
            }))}
            value={loai}
            onChange={(v) => setLoai(v as LoaiDonVi | undefined)}
          />
          <Select
            allowClear
            className="min-w-[10rem]"
            placeholder={t("vanBanPhanCong.tatCaTrangThai")}
            aria-label={t("vanBanPhanCong.locTrangThai")}
            options={TRANG_THAI_NHIEM_VU.map((value) => ({
              value,
              label: t(NHAN_TRANG_THAI_NHIEM_VU[value]),
            }))}
            value={trangThai}
            onChange={(v) => setTrangThai(v as TrangThaiNhiemVu | undefined)}
          />
        </div>

        <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          {error ? (
            <div
              role="alert"
              className="flex items-center justify-between gap-3 px-4 py-3"
            >
              <span className="text-sm text-danger-700">
                {t("vanBanPhanCong.loiDanhSach", { loi: dichLoi(error) })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
              >
                {t("chung.thuLai")}
              </Button>
            </div>
          ) : (
            <DataTable<NhiemVuPhanCong>
              caption={t("vanBanPhanCong.bangCaption")}
              rowKey="id"
              dataSource={rows}
              columns={columns}
              loading={isLoading || dangDocId !== null}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              onRow={(a) =>
                canUpdate
                  ? { onClick: () => moSua(a.id), className: "cursor-pointer" }
                  : {}
              }
              locale={{
                emptyText: isLoading ? (
                  " "
                ) : (
                  <Empty
                    icon={OrderedListOutlined}
                    title={t(
                      coLoc
                        ? "vanBanPhanCong.rongLoc"
                        : "vanBanPhanCong.rongThat",
                    )}
                    description={
                      coLoc ? undefined : t("vanBanPhanCong.rongThatMo")
                    }
                    action={
                      coLoc || !canCreate ? undefined : (
                        <Button
                          variant="primary"
                          icon={<PlusOutlined />}
                          onClick={moThem}
                        >
                          {t("vanBanPhanCong.nutThemTay")}
                        </Button>
                      )
                    }
                  />
                ),
              }}
            />
          )}
        </div>
      </div>

      <NhiemVuTayDialog
        open={moForm}
        nhiemVu={dangSua}
        donVi={donVi.data?.data ?? []}
        saving={luu.isPending}
        loiMayChu={luu.error}
        onClose={() => setMoForm(false)}
        onSubmit={(vao) =>
          luu.mutate(
            { id: dangSua?.id ?? null, vao },
            { onSuccess: () => setMoForm(false) },
          )
        }
      />
      {moTai > 0 && (
        <PhanCongTepDialog key={moTai} open onClose={() => setMoTai(0)} />
      )}
    </div>
  );
}
